"use client";

import { Controller } from "react-hook-form";
import { ChevronRight, Mail, ArrowLeft, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useOtpAuth } from "@/components/auth/otp-auth/use-otp-auth";
import { recordMasterclassLead } from "@/actions/masterclass/record-masterclass-lead";
interface VslEmailCaptureFormProps {
  slug: string;
  source: string;
  ctaLabel: string;
  id?: string;
}

export function VslEmailCaptureForm({
  slug,
  source,
  ctaLabel,
  id,
}: VslEmailCaptureFormProps) {
  const {
    step,
    email,
    countdown,
    isSubmitting,
    error,
    emailForm,
    otpForm,
    handleEmailSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleBack,
  } = useOtpAuth({
    redirectTo: `/clase/${slug}/ver`,
    onAuthenticated: () => recordMasterclassLead(source),
    funnelEnterSlug: slug,
    metaLeadContentName: `vsl_${slug}`,
  });

  return (
    <div id={id} className="flex flex-col gap-4">
      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-center text-sm text-red-400">
          {error}
        </div>
      )}

      {step === "email" && (
        <form
          onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
          className="flex flex-col gap-4"
        >
          <Controller
            name="email"
            control={emailForm.control}
            render={({ field, fieldState }) => (
              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                  <Input
                    type="email"
                    placeholder="email@email.com"
                    className="h-12 border-white/10 bg-white/5 pl-10 text-white placeholder:text-white/30"
                    disabled={isSubmitting}
                    {...field}
                  />
                </div>
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-400">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <Button
            type="submit"
            size="lg"
            className="bg-brand-red hover:bg-brand-red/90 h-12 w-full text-base font-semibold text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                {ctaLabel}
                <ChevronRight className="h-5 w-5" />
              </>
            )}
          </Button>
        </form>
      )}

      {step === "otp" && (
        <form
          onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
          className="flex flex-col gap-4"
          id={`otp-form-${id}`}
        >
          <div className="text-center">
            <p className="text-sm text-white/60">
              Enviamos un código de 6 dígitos a
            </p>
            <p className="mt-1 font-semibold text-white">{email}</p>
            <p className="mt-2 text-xs text-white/40">
              Si no lo ves, revisa spam o promociones.
            </p>
          </div>

          <Controller
            name="otp"
            control={otpForm.control}
            render={({ field }) => (
              <div className="flex justify-center py-2">
                <InputOTP
                  containerClassName="mx-auto w-fit"
                  maxLength={6}
                  value={field.value}
                  onChange={(value: string) => {
                    const sanitized = value.replace(/\D/g, "");
                    field.onChange(sanitized);
                    if (sanitized.length === 6 && !isSubmitting) {
                      setTimeout(() => {
                        const form = document.getElementById(
                          `otp-form-${id}`,
                        ) as HTMLFormElement;
                        form?.requestSubmit();
                      }, 100);
                    }
                  }}
                  onPaste={(e: React.ClipboardEvent) => {
                    e.preventDefault();
                    const sanitized = e.clipboardData
                      .getData("text")
                      .replace(/\D/g, "")
                      .slice(0, 6);
                    field.onChange(sanitized);
                    if (sanitized.length === 6 && !isSubmitting) {
                      setTimeout(() => {
                        const form = document.getElementById(
                          `otp-form-${id}`,
                        ) as HTMLFormElement;
                        form?.requestSubmit();
                      }, 100);
                    }
                  }}
                  pattern="^[0-9]*$"
                  disabled={isSubmitting}
                >
                  <InputOTPGroup className="gap-1 sm:gap-2">
                    <InputOTPSlot
                      index={0}
                      className="h-10 w-10 border-white/20 bg-white/5 text-lg text-white sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={1}
                      className="h-10 w-10 border-white/20 bg-white/5 text-lg text-white sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={2}
                      className="h-10 w-10 border-white/20 bg-white/5 text-lg text-white sm:h-12 sm:w-12 sm:text-xl"
                    />
                  </InputOTPGroup>
                  <InputOTPSeparator className="text-white/30" />
                  <InputOTPGroup className="gap-1 sm:gap-2">
                    <InputOTPSlot
                      index={3}
                      className="h-10 w-10 border-white/20 bg-white/5 text-lg text-white sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={4}
                      className="h-10 w-10 border-white/20 bg-white/5 text-lg text-white sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={5}
                      className="h-10 w-10 border-white/20 bg-white/5 text-lg text-white sm:h-12 sm:w-12 sm:text-xl"
                    />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            )}
          />

          <Button
            type="submit"
            size="lg"
            className="bg-brand-red hover:bg-brand-red/90 h-12 w-full text-base font-semibold text-white"
            disabled={isSubmitting || otpForm.watch("otp").length !== 6}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Verificando...
              </>
            ) : (
              "Verificar código"
            )}
          </Button>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              className="flex items-center gap-1 text-sm text-white/50 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-3 w-3" />
              Volver
            </button>

            <button
              type="button"
              onClick={handleResendOtp}
              disabled={countdown > 0 || isSubmitting}
              className="text-sm text-white/50 transition-colors hover:text-white disabled:opacity-40"
            >
              {countdown > 0 ? `Reenviar en ${countdown}s` : "Reenviar código"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
